import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { getAuth, multiFactor, TotpMultiFactorGenerator } from 'firebase/auth';

import { AppText, Button, IconBox } from '@zippeditoolsjs/ui-web';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import BackLink from '../../ui/controls/BackLink';
import {
  TotpQrPanel,
  TotpManualKey,
  TotpSetupSteps,
  TotpSuccess,
} from '../../ui/totp';
import OtpCodeInput from '../../utils/OtpCodeInput';
import AddSecondFactorOptIn from './AddSecondFactorOptIn';
import { getSafeErrorIdentifier } from '../../utils/getSafeErrorIdentifier';

export const STEPS = {
  QR: 'qr',
  CONFIRM: 'confirm',
  SUCCESS: 'success',
};

// There is no session cookie yet at this point, so enrollment goes through the
// Firebase client API on the currentUser that resolveSignIn just signed in.
export default function TotpEnrollment({
  tenantID,
  onDone,
  onEnrolled,
  onBack,
  onStepChange,
  setLoginErrorKey,
  offerFactor,
  optInChecked,
  onToggleOptIn,
  showBackToChooser,
  busy,
}) {
  const { t } = useTranslation();
  const showOptIn = Boolean(offerFactor) && Boolean(onToggleOptIn);
  const continueToOfferedFactor = showOptIn && Boolean(optInChecked);
  const [step, setStep] = useState(STEPS.QR);
  // Everything on the account after enroll, not just what was added this session.
  const [successFactors, setSuccessFactors] = useState([]);
  const [secret, setSecret] = useState(null);
  const [qrUri, setQrUri] = useState('');
  const [code, setCode] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const locked = enrolling || Boolean(busy);
  const otpRef = useRef();

  // Lets a parent mirror the internal step in its own stepper.
  useEffect(() => {
    onStepChange && onStepChange(step);
  }, [step, onStepChange]);

  useEffect(() => {
    let cancelled = false;
    async function generateSecret() {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        // The signed-in user's own tenant, never the screen's: the secret has
        // to be generated where the sign-in already landed.
        auth.tenantId = (user && user.tenantId) || tenantID;
        const session = await multiFactor(user).getSession();
        const totpSecret = await TotpMultiFactorGenerator.generateSecret(session);
        if (cancelled) return;
        setSecret(totpSecret);
        setQrUri(totpSecret.generateQrCodeUrl(user.email, 'Zippedi'));
      } catch (error) {
        console.error(
          'TotpEnrollment.generateSecret:',
          getSafeErrorIdentifier(error),
        );
        if (!cancelled) setLoginErrorKey('heimdal_app.Login.try_again_later');
      }
    }
    generateSecret();
    return () => {
      cancelled = true;
    };
    // One secret per mount: re-running would strand the QR already being scanned.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enrollWithCode = async verificationCode => {
    if (!secret || verificationCode.length < 6 || locked) return;
    setEnrolling(true);
    setLoginErrorKey(null);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      auth.tenantId = (user && user.tenantId) || tenantID;
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        secret,
        verificationCode,
      );
      await multiFactor(user).enroll(assertion, 'Authenticator app');
      if (continueToOfferedFactor) {
        onEnrolled();
      } else {
        // enrolledFactors stays stale until the user is reloaded.
        try {
          await user.reload();
        } catch {
          // Fall back to the cached user; worst case is a slightly stale badge.
        }
        const factors = multiFactor(user).enrolledFactors.map(f =>
          f.factorId === TotpMultiFactorGenerator.FACTOR_ID ? 'totp' : 'sms',
        );
        setSuccessFactors(factors);
        setStep(STEPS.SUCCESS);
      }
    } catch (error) {
      setLoginErrorKey(
        error.code === 'auth/invalid-verification-code'
          ? 'heimdal_app.Login.wrong_code'
          : 'heimdal_app.Login.try_again_later',
      );
      setCode('');
      if (otpRef.current) otpRef.current.reset();
    }
    setEnrolling(false);
  };

  if (step === STEPS.SUCCESS) {
    // Auto-enter because finishing here drops the user straight into the app.
    return (
      <TotpSuccess
        autoEnter
        seconds={5}
        onEnter={onDone}
        enrolledFactors={successFactors}
      />
    );
  }

  if (step === STEPS.CONFIRM) {
    return (
      <div className="flex flex-col">
        <BackLink disabled={locked} onClick={() => setStep(STEPS.QR)} />
        <IconBox className="mb-4">
          <IconComponent iconName="shield-checkmark" style={{ fontSize: 22 }} />
        </IconBox>
        <AppText variant="titleXLarge" as="h3" className="mb-2">
          {t('heimdal_app.TOTP.totp_code_title')}
        </AppText>
        <AppText variant="bodyMedium" as="p" className="mb-6 text-secondary">
          {t('heimdal_app.TOTP.enter_code')}
        </AppText>

        <div className="mb-3.5">
          {/* No auto-submit on the 6th digit: enrolling is an explicit press. */}
          <OtpCodeInput
            ref={otpRef}
            label={t('heimdal_app.TOTP.totp_code_title')}
            disabled={locked}
            onChange={setCode}
          />
        </div>
        <p className="m-0 mb-3.5 font-sans text-xs text-muted">
          {t('heimdal_app.TOTP.code_renews')}
        </p>

        {showOptIn ? (
          <AddSecondFactorOptIn
            factor={offerFactor}
            checked={Boolean(optInChecked)}
            onToggle={onToggleOptIn}
            disabled={locked}
          />
        ) : null}

        <Button
          fullWidth
          loading={enrolling}
          disabled={code.length < 6 || locked}
          onClick={() => enrollWithCode(code)}>
          {showOptIn && optInChecked
            ? t('heimdal_app.TOTP.verify_and_add')
            : t('heimdal_app.TOTP.verify')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* From the chooser, back returns there to pick SMS instead; otherwise the
          only way out is the "maybe later" exit below, which releases the session. */}
      {showBackToChooser ? <BackLink disabled={locked} onClick={onBack} /> : null}

      <AppText variant="titleXLarge" as="h3" className="mb-2">
        {t('heimdal_app.TOTP.setup_title')}
      </AppText>
      <TotpSetupSteps className="mb-[18px] mt-1.5" />

      <TotpQrPanel uri={qrUri} className="mb-4" />

      <TotpManualKey secret={secret?.secretKey} className="mb-[18px]" />

      <Button
        fullWidth
        disabled={!qrUri || locked}
        onClick={() => setStep(STEPS.CONFIRM)}>
        {t('heimdal_app.TOTP.scanned_continue')}
      </Button>
      {!showBackToChooser ? (
        <button
          type="button"
          onClick={onBack}
          disabled={locked}
          className="mt-3.5 flex cursor-pointer items-center justify-center gap-1.5 border-0 bg-transparent p-1 font-sans text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted transition-colors hover:text-accent disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:text-muted">
          {t('heimdal_app.TOTP.offer_later')}
        </button>
      ) : null}
    </div>
  );
}

TotpEnrollment.propTypes = {
  tenantID: PropTypes.string.isRequired,
  onDone: PropTypes.func.isRequired,
  onEnrolled: PropTypes.func,
  onBack: PropTypes.func.isRequired,
  onStepChange: PropTypes.func,
  setLoginErrorKey: PropTypes.func.isRequired,
  offerFactor: PropTypes.oneOf(['sms']),
  optInChecked: PropTypes.bool,
  onToggleOptIn: PropTypes.func,
  showBackToChooser: PropTypes.bool,
  busy: PropTypes.bool,
};
